import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/auth";

/* /smartket/marketer/new — Marketer creates or edits their public
   profile. This is what founders will browse when picking a marketer
   to trust. Kept lightweight for launch: name, firm, niches, reach,
   past wins, portfolio links. */

const NICHE_SUGGESTIONS = [
  "TikTok", "Instagram", "YouTube", "Paid Ads", "SEO", "Email",
  "Community", "Influencer", "Cold Outreach", "Content", "Reddit",
  "X / Twitter", "Podcast", "B2B", "DTC", "SaaS", "Local", "Farming",
];

export default function SmartKetMarketerNew() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [displayName, setDisplayName] = useState("");
  const [firmName, setFirmName] = useState("");
  const [niches, setNiches] = useState([]);
  const [reach, setReach] = useState("");
  const [wins, setWins] = useState("");
  const [urls, setUrls] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const prev = document.title;
    document.title = "SmartKet — Build your marketer profile";
    return () => { document.title = prev; };
  }, []);

  useEffect(() => {
    if (!user) { setLoaded(true); return; }
    axios.get(`${API}/smartket/marketers/mine`).then(r => {
      const p = r.data?.profile;
      if (p) {
        setDisplayName(p.display_name || "");
        setFirmName(p.firm_name || "");
        setNiches(p.niches || []);
        setReach(p.reach_description || "");
        setWins(p.past_wins || "");
        setUrls((p.portfolio_urls || []).join("\n"));
      }
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [user]);

  const toggleNiche = (n) => {
    setNiches(v => v.includes(n) ? v.filter(x => x !== n) : (v.length < 8 ? [...v, n] : v));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setErr(""); setBusy(true);
    try {
      await axios.post(`${API}/smartket/marketers`, {
        display_name: displayName.trim(),
        firm_name: firmName.trim() || null,
        niches,
        reach_description: reach.trim(),
        past_wins: wins.trim() || null,
        portfolio_urls: urls.split("\n").map(u => u.trim()).filter(Boolean),
      });
      nav("/smartket/marketers");
    } catch (e2) {
      setErr(e2.response?.data?.detail || "couldn't save profile");
    }
    setBusy(false);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0d1410] text-white p-8 max-w-lg mx-auto">
        <div className="font-mono text-sm">Sign in first to build your marketer profile.</div>
        <Link to="/login?next=/smartket/marketer/new" data-testid="need-login" className="mt-4 inline-block bg-[#d4a34a] text-black font-mono text-xs tracking-widest px-3 py-2">SIGN IN ▸</Link>
      </div>
    );
  }

  const F = "w-full bg-black/40 border border-white/20 focus:border-[#d4a34a] px-3 py-2 text-white text-sm font-mono outline-none";

  return (
    <div data-testid="marketer-new-page" className="min-h-screen bg-[#0d1410] text-white">
      <div className="border-b-2 border-[#d4a34a]/40 bg-[#141a15]">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
          <Link to="/smartket/join" className="font-mono text-[10px] tracking-[0.4em] text-white/60 hover:text-[#d4a34a]">
            ← SMARTKET · JOIN
          </Link>
          <h1 className="mt-3 font-display text-4xl md:text-5xl leading-none tracking-tight">
            YOUR <span className="text-[#d4a34a]">MARKETER</span> PROFILE
          </h1>
          <p className="mt-3 font-mono text-sm text-white/75 max-w-xl">
            Founders will read this before agreeing to a slip. Be real. Be short. Show what you're actually good at.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="max-w-3xl mx-auto px-4 md:px-8 py-8 space-y-5" data-testid="marketer-form">
        {err && <div className="font-mono text-xs text-yt-red border border-yt-red/50 p-2">{err}</div>}
        {!loaded && <div className="font-mono text-xs text-white/50">loading your existing profile…</div>}

        <div>
          <label className="font-mono text-[10px] tracking-widest text-white/70 block mb-1">
            YOUR NAME OR HANDLE <span className="text-yt-red">*</span>
          </label>
          <input value={displayName} onChange={e => setDisplayName(e.target.value)} required maxLength={80}
                 placeholder="e.g. Sam Rivera or @growthsam" data-testid="mf-name" className={F} />
        </div>

        <div>
          <label className="font-mono text-[10px] tracking-widest text-white/70 block mb-1">
            FIRM / AGENCY NAME (OPTIONAL)
          </label>
          <input value={firmName} onChange={e => setFirmName(e.target.value)} maxLength={120}
                 placeholder="Leave blank if you work solo" data-testid="mf-firm" className={F} />
        </div>

        <div>
          <label className="font-mono text-[10px] tracking-widest text-white/70 block mb-2">
            YOUR NICHES <span className="text-white/40">(pick up to 8)</span>
          </label>
          <div className="flex flex-wrap gap-2">
            {NICHE_SUGGESTIONS.map(n => (
              <button
                key={n}
                type="button"
                data-testid={`niche-${n}`}
                onClick={() => toggleNiche(n)}
                className={`font-mono text-[11px] tracking-widest px-2 py-1 border transition-colors ${
                  niches.includes(n)
                    ? "bg-[#d4a34a] text-black border-[#d4a34a]"
                    : "bg-transparent text-white/70 border-white/20 hover:border-white/60"
                }`}
              >
                {n.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="mt-2 font-mono text-[10px] text-white/40">selected: {niches.length}/8</div>
        </div>

        <div>
          <label className="font-mono text-[10px] tracking-widest text-white/70 block mb-1">
            REACH / AUDIENCE <span className="text-yt-red">*</span>
          </label>
          <textarea value={reach} onChange={e => setReach(e.target.value)} required maxLength={500} rows={3}
                    placeholder="e.g. '12k TikTok followers in small-farm niche, run a 3k email list of DTC founders...'"
                    data-testid="mf-reach" className={F} />
        </div>

        <div>
          <label className="font-mono text-[10px] tracking-widest text-white/70 block mb-1">
            PAST WINS (OPTIONAL)
          </label>
          <textarea value={wins} onChange={e => setWins(e.target.value)} maxLength={800} rows={3}
                    placeholder="e.g. 'Grew a Shopify brand from $8k to $60k/mo in 5 months using UGC + retargeting.'"
                    data-testid="mf-wins" className={F} />
        </div>

        <div>
          <label className="font-mono text-[10px] tracking-widest text-white/70 block mb-1">
            PORTFOLIO / SOCIAL LINKS <span className="text-white/40">(one per line, up to 6)</span>
          </label>
          <textarea value={urls} onChange={e => setUrls(e.target.value)} rows={4}
                    placeholder="https://tiktok.com/@you&#10;https://linkedin.com/in/you"
                    data-testid="mf-urls" className={F + " font-mono"} />
        </div>

        <button type="submit" disabled={busy || !displayName.trim() || !reach.trim()} data-testid="mf-submit"
                className="bg-[#d4a34a] text-black font-display text-base tracking-widest px-5 py-3 shadow-brutal hover:bg-white disabled:opacity-40">
          {busy ? "saving…" : "SAVE PROFILE"}
        </button>
      </form>
    </div>
  );
}
